import {isBefore} from 'date-fns';
import {DataStatus, Form, FormField, ValidationErrors} from 'platform/components';
import {VStack} from 'platform/foundation';

import {UseFormReset, UseFormReturn} from 'react-hook-form';

import {defaultTo} from 'ramda';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetSuppliersQuery} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {
  DateRangeField,
  DEFAULT_RANGE_VALUE,
  formatExternalFiltersDate,
  getOptionsFromManufacturers,
  getOptionsFromSuppliers,
  RangeField,
} from '@dms/shared';
import {NumberRangeField} from '@dms/warehouse/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

type MaterialPurchaseListTabExternalFiltersForm = Partial<{
  catalogNumber: string;
  manufacturerId: string;
  supplierId: string;
  created: DateRangeField;
  neededAt: DateRangeField;
  neededIn: NumberRangeField;
}>;

type MaterialPurchaseListTabExternalFiltersProps = FormToDataGridConnectorProps & TestIdProps;

function isDateRangeField(value: unknown): value is DateRangeField {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return 'from' in value || 'to' in value;
}

function isNumberRangeField(value: unknown): value is NumberRangeField {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return 'from' in value || 'to' in value;
}

export function MaterialPurchaseListTabExternalFilters(
  props: MaterialPurchaseListTabExternalFiltersProps
) {
  const {
    data: manufacturers,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
  } = useGetSuppliersQuery();

  const isLoading = isManufacturersLoading || isSuppliersLoading;
  const isError = isManufacturersError || isSuppliersError;

  const rawCreated = props.values?.created;
  const rawNeededAt = props.values?.neededAt;
  const rawNeededIn = props.values?.neededIn;

  const values: MaterialPurchaseListTabExternalFiltersForm = {
    ...props.values,
    created: defaultTo(DEFAULT_RANGE_VALUE, isDateRangeField(rawCreated) ? rawCreated : undefined),
    neededAt: defaultTo(
      DEFAULT_RANGE_VALUE,
      isDateRangeField(rawNeededAt) ? rawNeededAt : undefined
    ),
    neededIn: defaultTo(
      DEFAULT_RANGE_VALUE,
      isNumberRangeField(rawNeededIn) ? rawNeededIn : undefined
    ),
  };

  const handleChange = (
    values: MaterialPurchaseListTabExternalFiltersForm,
    _setErrors: (errors: ValidationErrors | null) => void,
    _reset: UseFormReset<MaterialPurchaseListTabExternalFiltersForm>,
    formApi: UseFormReturn<MaterialPurchaseListTabExternalFiltersForm>
  ) => {
    const created = formApi.getValues('created');
    const neededAt = formApi.getValues('neededAt');

    const isCreatedInvalid =
      created?.to && created?.from && isBefore(parseDate(created.to), parseDate(created.from));

    if (isCreatedInvalid) {
      formApi.setError('created.from', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
      formApi.setError('created.to', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
    } else {
      formApi.clearErrors('created.from');
      formApi.clearErrors('created.to');
    }

    const isNeededAtInvalid =
      neededAt?.to && neededAt?.from && isBefore(parseDate(neededAt.to), parseDate(neededAt.from));

    if (isNeededAtInvalid) {
      formApi.setError('neededAt.from', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
      formApi.setError('neededAt.to', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
    } else {
      formApi.clearErrors('neededAt.from');
      formApi.clearErrors('neededAt.to');
    }

    const isFormInvalid = isCreatedInvalid || isNeededAtInvalid;

    if (isFormInvalid) {
      return;
    }

    const formattedNeededAt = {
      from: formatExternalFiltersDate(values.neededAt?.from),
      to: formatExternalFiltersDate(values.neededAt?.to),
    };

    const formattedCreated = {
      from: formatExternalFiltersDate(values.created?.from),
      to: formatExternalFiltersDate(values.created?.to),
    };

    props.onChange({
      ...values,
      neededAt: formattedNeededAt,
      created: formattedCreated,
    });
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<MaterialPurchaseListTabExternalFiltersForm> values={values} onChange={handleChange}>
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="catalogNumber"
              type="text"
              label={i18n.t('entity.warehouse.labels.catalogueNumber')}
              data-testid={suffixTestId('catalogueNumber', props)}
            />
            <FormField
              control={control}
              name="manufacturerId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.manufacturer')}
              options={getOptionsFromManufacturers(manufacturers)}
              data-testid={suffixTestId('manufacturer', props)}
            />
            <FormField
              control={control}
              name="supplierId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.supplier')}
              options={getOptionsFromSuppliers(suppliers)}
              data-testid={suffixTestId('supplier', props)}
            />
            <RangeField
              control={control}
              type="date"
              name="created"
              labelFrom={i18n.t('general.labels.createdFrom')}
              labelTo={i18n.t('general.labels.createdTo')}
              data-testid={suffixTestId('inputs.created', props)}
            />
            <RangeField
              control={control}
              type="date"
              name="neededAt"
              labelFrom={i18n.t('general.labels.neededAtFrom')}
              labelTo={i18n.t('general.labels.neededAtTo')}
              data-testid={suffixTestId('inputs.neededAt', props)}
            />
            <RangeField
              control={control}
              type="number"
              name="neededIn"
              labelFrom={i18n.t('general.labels.neededInFrom')}
              labelTo={i18n.t('general.labels.neededInTo')}
              data-testid={suffixTestId('inputs.neededIn', props)}
            />
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
